import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  Timestamp,
  writeBatch,
  onSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';
import { User } from 'firebase/auth';

export type ChangelogCategory = 'feature' | 'improvement' | 'bugfix' | 'announcement';
export type ChangelogStatus = 'draft' | 'published' | 'suggested';

export interface ChangelogEntry {
  id?: string;
  title: string;
  content: string; // Markdown content
  category: ChangelogCategory;
  status: ChangelogStatus;
  publishedAt?: Timestamp | null;
  createdAt: Timestamp | ReturnType<typeof serverTimestamp>;
  createdBy: string;
  updatedAt?: Timestamp | ReturnType<typeof serverTimestamp>;
  showAsAnnouncement?: boolean; // If true, show as in-app announcement
  announcementDismissible?: boolean; // If true, users can dismiss the announcement
  imageUrl?: string; // Optional hero image
  tags?: string[]; // Optional tags for filtering
  // For suggested entries from GitHub
  githubCommitSha?: string;
  githubRepoOwner?: string;
  githubRepoName?: string;
  githubCommitUrl?: string;
  teamId?: string; // Associated team if this is a team-specific suggestion
  reviewNote?: string; // Safety review reason when auto-publish flags content as unsafe
}

export interface ChangelogDismissal {
  id?: string;
  userId: string;
  entryId: string;
  dismissedAt: Timestamp | ReturnType<typeof serverTimestamp>;
}

// Render-time helper: strip common Markdown syntax so changelog content can be
// used in plain-text contexts (banners, toasts, sidebar previews) without
// leaking `**`, `#`, list markers, or link syntax.
export function stripChangelogMarkdown(text: string): string {
  return text
    .replace(/#{1,6}\s+/g, '')              // headings
    .replace(/\*\*(.+?)\*\*/g, '$1')        // bold
    .replace(/\*(.+?)\*/g, '$1')            // italic
    .replace(/__(.+?)__/g, '$1')            // bold alt
    .replace(/_(.+?)_/g, '$1')              // italic alt
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/`(.+?)`/g, '$1')              // inline code
    .replace(/^[-*+]\s+/gm, '')             // list items
    .replace(/^\d+\.\s+/gm, '')             // numbered lists
    .replace(/\n{2,}/g, ' ')                // collapse double newlines
    .replace(/\n/g, ' ')                    // remaining newlines
    .trim();
}

// Create a new changelog entry
export async function createChangelogEntry(
  entry: Omit<ChangelogEntry, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>,
  creator: User
) {
  const entryData = {
    ...entry,
    createdBy: creator.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, 'changelog'), entryData);
  return docRef.id;
}

// Get all published changelog entries (public)
export async function getPublishedChangelogEntries(limitCount = 50) {
  const q = query(
    collection(db, 'changelog'),
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc'),
    firestoreLimit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as ChangelogEntry[];
}

// Get all changelog entries (admin only)
export async function getAllChangelogEntries() {
  const q = query(
    collection(db, 'changelog'),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as ChangelogEntry[];
}

// Get changelog entry by ID
export async function getChangelogEntry(entryId: string) {
  const docRef = doc(db, 'changelog', entryId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data()
  } as ChangelogEntry;
}

// Update changelog entry
export async function updateChangelogEntry(
  entryId: string,
  updates: Partial<ChangelogEntry>
) {
  const docRef = doc(db, 'changelog', entryId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
}

// Publish a changelog entry
export async function publishChangelogEntry(entryId: string) {
  const docRef = doc(db, 'changelog', entryId);
  await updateDoc(docRef, {
    status: 'published',
    publishedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}

// Delete changelog entry
export async function deleteChangelogEntry(entryId: string) {
  const docRef = doc(db, 'changelog', entryId);
  await deleteDoc(docRef);
}

// Check if user has dismissed an announcement
export async function hasUserDismissedAnnouncement(
  userId: string,
  entryId: string
): Promise<boolean> {
  const dismissalRef = doc(db, 'changelog', entryId, 'dismissals', userId);
  const dismissalSnap = await getDoc(dismissalRef);
  return dismissalSnap.exists();
}

// Dismiss an announcement
export async function dismissAnnouncement(userId: string, entryId: string) {
  const dismissalRef = doc(db, 'changelog', entryId, 'dismissals', userId);
  await setDoc(dismissalRef, {
    userId,
    entryId,
    dismissedAt: serverTimestamp()
  });
}

// Get active announcements for a user (not dismissed)
export async function getActiveAnnouncementsForUser(userId: string) {
  // Get all published entries with showAsAnnouncement
  const q = query(
    collection(db, 'changelog'),
    where('status', '==', 'published'),
    where('showAsAnnouncement', '==', true),
    orderBy('publishedAt', 'desc')
  );

  const snapshot = await getDocs(q);
  const entries = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as ChangelogEntry[];

  // Filter out dismissed announcements
  const activeEntries = [];
  for (const entry of entries) {
    const isDismissed = await hasUserDismissedAnnouncement(userId, entry.id!);
    if (!isDismissed) {
      activeEntries.push(entry);
    }
  }

  return activeEntries;
}

// Subscribe to active announcements for a user
export function subscribeToActiveAnnouncements(
  userId: string,
  callback: (announcements: ChangelogEntry[]) => void
) {
  const q = query(
    collection(db, 'changelog'),
    where('status', '==', 'published'),
    where('showAsAnnouncement', '==', true),
    orderBy('publishedAt', 'desc')
  );

  return onSnapshot(q, async (snapshot) => {
    const entries = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ChangelogEntry[];

    // Filter out dismissed announcements
    const activeEntries = [];
    for (const entry of entries) {
      const isDismissed = await hasUserDismissedAnnouncement(userId, entry.id!);
      if (!isDismissed) {
        activeEntries.push(entry);
      }
    }

    callback(activeEntries);
  }, (error) => {
    console.error('Error listening to active announcements:', error);
    callback([]);
  });
}

// Create suggested entry from GitHub commit (for team-specific repos)
export async function createSuggestedEntryFromCommit(
  teamId: string,
  commit: {
    sha: string;
    message: string;
    url: string;
    repoOwner: string;
    repoName: string;
  },
  creator: User
) {
  const entry: Omit<ChangelogEntry, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'> = {
    title: commit.message.split('\n')[0], // First line as title
    content: commit.message,
    category: 'improvement', // Default category
    status: 'suggested',
    githubCommitSha: commit.sha,
    githubRepoOwner: commit.repoOwner,
    githubRepoName: commit.repoName,
    githubCommitUrl: commit.url,
    teamId,
    showAsAnnouncement: false,
  };

  return createChangelogEntry(entry, creator);
}

// Get suggested entries for a team
export async function getTeamSuggestedEntries(teamId: string) {
  const q = query(
    collection(db, 'changelog'),
    where('status', '==', 'suggested'),
    where('teamId', '==', teamId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as ChangelogEntry[];
}
